import React, { ComponentProps } from "react";
import { spacing } from "../../stitches.config";
import { Tag as StyledTag } from "./__styles__/Tag";
import { Icon, IconNames } from "./Icons/LucideIcons";
import { Body } from "./Typography";

const IconColorMap = {
  default: "contentInfo",
  neutral: "contentSecondary",
  warning: "contentAttentionAccessible",
  critical: "contentCritical",
  success: "contentSuccess",
  neutralInverted: "contentSecondaryInverted",
} as const;

type Size = "default" | "in-line";

export type StyleVariant =
  | "default"
  | "neutral"
  | "warning"
  | "critical"
  | "success"
  | "neutralInverted";

export type TagProps = Omit<
  ComponentProps<typeof StyledTag>,
  "css" | "styleVariant" | "onClick" | "disabled" | "size"
> & {
  styleVariant: StyleVariant;
  leftIconName?: IconNames;
  size?: Size;
  loading?: boolean;
  onClose?: () => void;
  disabled?: boolean;
  "data-testid"?: string;
};

export const Tag = ({
  leftIconName,
  size = "default",
  loading,
  styleVariant,
  onClose,
  disabled = false,
  ...props
}: TagProps) => {
  return (
    <StyledTag
      styleVariant={styleVariant}
      size={size}
      disabled={disabled}
      data-testid={props["data-testid"]}
    >
      {leftIconName && (
        <Icon
          iconName={leftIconName}
          color={IconColorMap[styleVariant]}
          size={12}
        />
      )}
      <Body
        type="emphasis"
        size="small"
        color={
          styleVariant === "neutralInverted" ?
            "contentPrimaryInverted"
          : "contentPrimary"
        }
      >
        {props.children}
      </Body>
      {onClose && (
        <button
          onClick={onClose}
          disabled={disabled}
          aria-label="Remove"
          style={{ paddingTop: spacing["2xs"].value }}
        >
          <Icon
            iconName="x"
            color={
              styleVariant === "neutralInverted" ?
                "contentSecondaryInverted"
              : "contentSecondary"
            }
            size={12}
            data-testid="tag-close-icon"
          />
        </button>
      )}
    </StyledTag>
  );
};
